import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for different marker types
const createCustomIcon = (color, type) => {
  if (type === 'ambulance') {
    const iconHtml = `<div style="
      background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 4px solid white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4), 0 2px 4px rgba(0,0,0,0.2);
      position: relative;
    ">
      <span style="color: white; font-size: 18px; font-weight: bold; z-index: 1;">🚑</span>
      <div style="
        position: absolute;
        top: -2px;
        right: -2px;
        width: 12px;
        height: 12px;
        background: #10B981;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      "></div>
    </div>`;
    
    return L.divIcon({
      html: iconHtml,
      className: 'custom-marker',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18]
    });
  } else if (type === 'patient') {
    // Attractive patient marker with vibrant emerald green gradient
    const iconHtml = `<div style="
      background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%);
      width: 44px;
      height: 44px;
      border-radius: 50%;
      border: 4px solid white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5), 0 3px 10px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.3);
      position: relative;
    ">
      <!-- Outer glow ring -->
      <div style="
        position: absolute;
        top: -8px;
        left: -8px;
        right: -8px;
        bottom: -8px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%);
        pointer-events: none;
      "></div>
      
      <!-- Human icon - more prominent and clear -->
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="z-index: 2; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
        <!-- Head -->
        <circle cx="12" cy="7" r="4.5" fill="white" stroke="rgba(5, 150, 105, 0.5)" stroke-width="1"/>
        <!-- Body/Torso -->
        <path d="M6 20C6 15.5817 8.58172 12 13 12C17.4183 12 20 15.5817 20 20" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="white"/>
      </svg>
      
      <!-- Active indicator badge -->
      <div style="
        position: absolute;
        top: -4px;
        right: -4px;
        width: 18px;
        height: 18px;
        background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 3px 8px rgba(245, 158, 11, 0.5), inset 0 1px 2px rgba(255,255,255,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 3;
      ">
        <div style="
          width: 7px;
          height: 7px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 1px 2px rgba(0,0,0,0.2);
        "></div>
      </div>
    </div>`;
    
    return L.divIcon({
      html: iconHtml,
      className: 'custom-marker patient-marker',
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      popupAnchor: [0, -22]
    });
  } else {
    // Default marker
    const iconHtml = `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
         <span style="color: white; font-size: 14px; font-weight: bold;">📍</span>
       </div>`;
    
    return L.divIcon({
      html: iconHtml,
      className: 'custom-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15]
    });
  }
};

// Component to handle map bounds when markers change
const MapBounds = ({ markers, autoZoom = true }) => {
  const map = useMap();
  
  useEffect(() => {
    // Add a small delay to ensure map is fully initialized
    const timeoutId = setTimeout(() => {
      // Check if map instance is valid and has the required methods
      if (!map || !map.setView || !map.fitBounds) {
        return;
      }
      
      // Check if map container exists and is properly initialized
      try {
        const container = map.getContainer();
        if (!container || !container._leaflet_id) {
          return;
        }
        
        // Check if map pane exists (critical for _leaflet_pos)
        const mapPane = map.getPane('mapPane');
        if (!mapPane) {
          return;
        }
        
        // Additional check: verify the map is actually rendered
        if (!map._loaded) {
          return;
        }
      } catch (error) {
        console.warn('Map not ready:', error);
        return;
      }
      
      if (autoZoom && markers && markers.length > 0) {
        const validMarkers = markers.filter(marker => 
          marker.position && 
          typeof marker.position.lat === 'number' && 
          typeof marker.position.lng === 'number'
        );
        
        if (validMarkers.length === 1) {
          // If only one marker, center on it
          try {
            map.setView([validMarkers[0].position.lat, validMarkers[0].position.lng], 15, {
              animate: false
            });
          } catch (error) {
            console.warn('Error setting map view:', error);
          }
        } else if (validMarkers.length > 1) {
          // If multiple markers, fit bounds to show all
          try {
            const bounds = L.latLngBounds(validMarkers.map(marker => [
              marker.position.lat, 
              marker.position.lng
            ]));
            map.fitBounds(bounds, { padding: [20, 20], animate: false });
          } catch (error) {
            console.warn('Error fitting map bounds:', error);
          }
        }
      }
    }, 100); // Small delay to ensure map is ready
    
    return () => clearTimeout(timeoutId);
  }, [map, markers, autoZoom]);
  
  return null;
};

// Main BaseMap component
const BaseMap = ({
  center = [40.7128, -74.0060], // Default to NYC
  zoom = 13,
  markers = [],
  autoZoom = true,
  height = '400px',
  className = '',
  onMapClick,
  showAttribution = true,
  zoomControl = true,
  scrollWheelZoom = true
}) => {
  const mapRef = useRef();

  const defaultCenter = Array.isArray(center) && center.length === 2 
    ? center 
    : [40.7128, -74.0060];

  // Only render map if we have valid center coordinates
  if (!defaultCenter || !Array.isArray(defaultCenter) || defaultCenter.length !== 2 || 
      isNaN(defaultCenter[0]) || isNaN(defaultCenter[1])) {
    return (
      <div className={`relative ${className}`} style={{ height }}>
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75">
          <div className="text-center">
            <p className="text-gray-600 text-sm">Invalid map center coordinates</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ height }}>
      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={zoomControl}
        scrollWheelZoom={scrollWheelZoom}
        onClick={onMapClick}
        key={`map-${defaultCenter[0]}-${defaultCenter[1]}-${zoom}`}
        whenReady={() => {
          // Map is ready - this helps prevent _leaflet_pos errors
          console.log('Map container is ready');
        }}
      >
        {/* OpenStreetMap tiles */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution={showAttribution ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' : ''}
          maxZoom={19}
        />
        
        {/* Auto-adjust bounds */}
        <MapBounds markers={markers} autoZoom={autoZoom} />
        
        {/* Render markers */}
        {markers.map((marker, index) => {
          if (!marker.position || 
              typeof marker.position.lat !== 'number' || 
              typeof marker.position.lng !== 'number') {
            return null;
          }
          
          const icon = marker.icon || createCustomIcon(
            marker.color || '#3B82F6', 
            marker.type || 'default'
          );
          
          return (
            <Marker
              key={marker.id || index}
              position={[marker.position.lat, marker.position.lng]}
              icon={icon}
            >
              {marker.popup && (
                <Popup>
                  <div className="p-2">
                    {typeof marker.popup === 'string' ? (
                      <p>{marker.popup}</p>
                    ) : (
                      marker.popup
                    )}
                  </div>
                </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Loading overlay */}
      {markers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Predefined icon colors
export const MARKER_COLORS = {
  AMBULANCE: '#EF4444', // Red
  PATIENT: '#10B981',   // Green (attractive emerald)
  HOSPITAL: '#3B82F6',  // Blue
  PICKUP: '#F59E0B',    // Amber
  DESTINATION: '#8B5CF6' // Purple
};

// Predefined marker types
export const MARKER_TYPES = {
  AMBULANCE: 'ambulance',
  PATIENT: 'patient',
  LOCATION: 'location'
};

export default BaseMap;
